import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import type * as Fabric from '@postermywall/fabricjs-2';
import {PickerInputField} from '@Components/color-picker-v2/components/picker-input-field';
import {CanvasColorPicker} from '@Components/color-picker-v2/components/canvas-color-picker';
import styles from './input-section.module.scss';
import type {RGBA} from '@Utils/color.util';
import {hexToRgba} from '@Utils/color.util';
import {AlphaPercentagePickerInputField} from '@Components/color-picker-v2/components/alpha-percentage-picker-input-field/alpha-percentage-picker-input-field';
import {EyedropperColorPicker, EyeDropperCTAType} from "@Components/eyedropper-color-picker/eyedropper-color-picker";

interface InputSectionProps {
  className?: string;
  color: RGBA;
  canvasRef?: Fabric.Canvas;
  canvasContainerRef?: JQuery<HTMLElement>;
  scale?: number;
  isAlphaDisabled?: boolean;

  onChangeColor(color: RGBA | null): void;

  onChangeEnded(color: RGBA | null): void;

  onChangeColorPickerMode?(state: boolean): void;

  showPicker?: boolean;
  showEyeDropperPicker?: boolean;
}

export function InputSection({
  isAlphaDisabled = false,
  className = '',
  scale = 1,
  onChangeColorPickerMode = (): void => {},
  showPicker = true,
  showEyeDropperPicker = false,
  ...props
}: InputSectionProps): ReactElement<any> | null {
  const containerRef = useRef<HTMLDivElement>(null);

  const getEyeDropperPicker = (): ReactElement<any> | null => {
    if (!showEyeDropperPicker || showPicker) {
      return null;
    }

    return (
      <EyedropperColorPicker
        onColorPicked={(val: string): void => {
          const color = hexToRgba(val);
          props.onChangeEnded(color);
        }}
        ctaType={EyeDropperCTAType.ICON}
      />
    );
  };

  return (
    <div ref={containerRef} className={`${className} spacing-m-t-5 flex-h-row`}>
      <div className={`${styles.inputContainer} radius-4 hover-transition-all`}>
        <div className={isAlphaDisabled ? styles.pickerInputWithoutAlpha : styles.pickerInput}>
          <PickerInputField
            inputContainerClassName={styles.inputContainerHeight}
            color={props.color}
            onChangeColor={(value: RGBA | null): void => {
              props.onChangeColor(value);
            }}
            onChangeEnded={(value: RGBA | null): void => {
              props.onChangeEnded(value);
            }}
            inputClassname={styles.pickerInputField}
          />
        </div>
        {!isAlphaDisabled ? <div className={`${styles.divider} _full-height`} /> : null}
        {!isAlphaDisabled ? (
          <div className={styles.alphaInput}>
            <AlphaPercentagePickerInputField
              className={styles.inputContainerHeight}
              color={props.color}
              onChangeColor={(value: RGBA | null): void => {
                props.onChangeColor(value);
              }}
              onChangeEnded={(value: RGBA | null): void => {
                props.onChangeEnded(value);
              }}
              inputClassName={styles.alphaInputField}
            />
          </div>
        ) : (
          []
        )}
      </div>
      {showPicker ? (
        <CanvasColorPicker
          className={styles.picker}
          color={props.color}
          onChangeColor={(value: RGBA | null): void => {
            props.onChangeColor(value);
          }}
          onChangeEnded={(value: RGBA | null): void => {
            props.onChangeEnded(value);
          }}
          canvasRef={props.canvasRef}
          canvasContainerRef={props.canvasContainerRef}
          scale={scale}
          onChangeColorPickerMode={(state): void => {
            onChangeColorPickerMode(state);
          }}
        />
      ) : null}
      {getEyeDropperPicker()}
    </div>
  );
}
